import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import AppShell from "@/components/layout/AppShell";
import { toast } from "sonner";
import { Loader2, ShieldAlert, Download, Play, Info, History } from "lucide-react";

// ─── Internal preview-only interface ──────────────────────────────
// This page is admin/operator-only. It manually drives one Sora 2 pilot
// generation at a time. It NEVER auto-generates, NEVER auto-retries,
// and REFUSES to run when the SDK capability check reports a blocker.
// See /app/backend/video_pilot.py for the wiring.
// ------------------------------------------------------------------

const TOKEN_KEY  = "nx_token";
const PENDING_KEY = "daam_pilot_pending_job";       // localStorage key
const IDEMP_KEY   = "daam_pilot_pending_idemp";     // localStorage key
const POLL_MS     = 3000;
const TERMINAL_STATUSES = ["ready", "failed"];

const getToken = () => localStorage.getItem(TOKEN_KEY) || "";
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

async function api(path, opts = {}) {
  const resp = await fetch(`${API_BASE}${path}`, {
    ...opts,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(opts.headers || {}),
    },
  });
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    // Prefer the classified message_ar/detail.message shape produced by
    // video_pilot_service.pilot_generate_video / router.
    const detail = body?.detail;
    const msg =
      (detail && (detail.message_ar || detail.message_en || detail.message)) ||
      body?.message ||
      resp.statusText ||
      "Request failed";
    const err = new Error(msg);
    err.code = detail?.code;
    err.status = resp.status;
    throw err;
  }
  return body;
}

// UUID good enough for a client-generated idempotency key.
const uuid = () =>
  (crypto.randomUUID && crypto.randomUUID()) ||
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });

const PLATFORMS = [
  { id: "tiktok",   label: "TikTok",   aspect: "9:16", size: "720x1280" },
  { id: "snapchat", label: "Snapchat", aspect: "9:16", size: "720x1280" },
  { id: "reels",    label: "Reels",    aspect: "9:16", size: "720x1280" },
  // Admin-only native landscape test option — 4 s single clip, no cropping.
  { id: "landscape_test",
    label: "اختبار أفقي 16:9 — ليس للنشر",
    aspect: "16:9", size: "1280x720",
    allowed_durations: [4], admin_only: true, test_only: true,
  },
];

const DURATIONS = [
  { id: 4, label: "4 ثانية", clip_count: 1 },
  { id: 8, label: "8 ثواني", clip_count: 2 },
];

export default function VideoPilotPage() {
  const [caps, setCaps] = useState(null);
  const [capsErr, setCapsErr] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [platform, setPlatform] = useState("tiktok");
  const [duration, setDuration] = useState(8);
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [cta, setCta] = useState("");
  const [watermark, setWatermark] = useState(true);
  const [estimate, setEstimate] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [dryResult, setDryResult] = useState(null);
  const [error, setError] = useState(null);

  // Async job state — single-active-job by design.
  const [job, setJob] = useState(null);       // current job doc from GET /job/{id}
  const [recent, setRecent] = useState([]);   // history from GET /jobs/mine
  const pollRef = useRef(null);
  const pending = job && !TERMINAL_STATUSES.includes(job.status);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pollOnce = useCallback(async (jobId) => {
    try {
      const doc = await api(`/api/preview/video-pilot/job/${jobId}`);
      setJob(doc);
      if (TERMINAL_STATUSES.includes(doc.status)) {
        stopPolling();
        localStorage.removeItem(PENDING_KEY);
        // Refresh the recent-jobs list so the finished item appears there.
        api("/api/preview/video-pilot/jobs/mine?limit=10")
          .then((r) => setRecent(r.jobs || []))
          .catch(() => {});
        if (doc.status === "ready") {
          toast.success("تم إنتاج الفيديو. اضغط تشغيل أو تنزيل.");
        } else {
          toast.error(doc.error_message || "فشل التوليد — لا إعادة محاولة تلقائية.");
        }
      }
    } catch (e) {
      // A single poll failure must NOT stop us — the pod restart or a
      // transient 502 could otherwise wedge the UI. Only stop on 404.
      if (e.status === 404) {
        stopPolling();
        localStorage.removeItem(PENDING_KEY);
        setJob(null);
      }
    }
  }, [stopPolling]);

  const startPolling = useCallback((jobId) => {
    stopPolling();
    pollOnce(jobId);
    pollRef.current = setInterval(() => pollOnce(jobId), POLL_MS);
  }, [pollOnce, stopPolling]);

  // 0. Mount-time recovery — first hydrate from localStorage (fastest);
  //    then ask the backend for the latest job in case the tab was
  //    opened on a new machine. Also fetch the recent-jobs list.
  useEffect(() => {
    const pendingId = localStorage.getItem(PENDING_KEY);
    if (pendingId) {
      startPolling(pendingId);
    } else {
      api("/api/preview/video-pilot/jobs/latest")
        .then((r) => {
          if (r.job) {
            setJob(r.job);
            if (!TERMINAL_STATUSES.includes(r.job.status)) {
              localStorage.setItem(PENDING_KEY, r.job.job_id);
              startPolling(r.job.job_id);
            }
          }
        })
        .catch(() => {});
    }
    api("/api/preview/video-pilot/jobs/mine?limit=10")
      .then((r) => setRecent(r.jobs || []))
      .catch(() => {});
    return stopPolling;
  }, [startPolling, stopPolling]);

  // 1. Load capability status on mount and every time the platform
  //    changes (each platform has its own native-aspect blocker set).
  useEffect(() => {
    const platMeta = PLATFORMS.find((p) => p.id === platform) || PLATFORMS[0];
    api(`/api/preview/video-pilot/capabilities?aspect=${encodeURIComponent(platMeta.aspect)}`)
      .then(setCaps)
      .catch((e) => setCapsErr(e.message));
  }, [platform]);

  // 1b. If the operator switches to a platform that hard-locks the
  //     allowed durations (landscape_test today), snap the current
  //     selection to the first allowed value so the UI cannot show a
  //     stale 8 s selection for a 4-s-only platform.
  useEffect(() => {
    const platMeta = PLATFORMS.find((p) => p.id === platform);
    const allowed = platMeta?.allowed_durations;
    if (allowed && !allowed.includes(duration)) setDuration(allowed[0]);
  }, [platform, duration]);

  // 2. Recompute estimate whenever a relevant input changes. Any input
  //    change also *revokes* the operator confirmation — no silent charges.
  useEffect(() => {
    setConfirmed(false);
    setEstimate(null);
    setError(null);
    if (!prompt.trim()) return;
    const body = JSON.stringify({
      prompt, platform, duration_seconds: duration,
      overlay_title: title, overlay_subtitle: subtitle, overlay_cta: cta,
      watermark,
    });
    api("/api/preview/video-pilot/estimate", { method: "POST", body })
      .then(setEstimate)
      .catch((e) => setError(e.message));
  }, [prompt, platform, duration, title, subtitle, cta, watermark]);

  const platMeta = useMemo(
    () => PLATFORMS.find((p) => p.id === platform) || PLATFORMS[0], [platform]);

  const canGenerate = !!(
    caps?.native_available &&
    estimate?.can_generate &&
    prompt.trim() &&
    confirmed &&
    !pending
  );

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setError(null);
    setDryResult(null);
    // Reuse an in-flight idempotency key if the browser was refreshed
    // mid-flight; otherwise mint a fresh one for this Generate click.
    let idempotency_key = localStorage.getItem(IDEMP_KEY);
    if (!idempotency_key || !pending) {
      idempotency_key = uuid();
      localStorage.setItem(IDEMP_KEY, idempotency_key);
    }
    try {
      const body = JSON.stringify({
        prompt, platform, duration_seconds: duration,
        overlay_title: title, overlay_subtitle: subtitle, overlay_cta: cta,
        watermark, confirm_charge: true,
        idempotency_key,
      });
      const r = await api("/api/preview/video-pilot/generate", {
        method: "POST", body,
      });
      if (r.job_id) {
        localStorage.setItem(PENDING_KEY, r.job_id);
        setJob({ job_id: r.job_id, status: r.status, prompt });
        startPolling(r.job_id);
        if (r.idempotent_hit) {
          toast.info("تم استرجاع الطلب الحالي بنفس المفتاح. لن يُصرف أي رصيد جديد.");
        } else {
          toast.success("بدأ التوليد. سيتم استطلاع الحالة تلقائياً حتى الاكتمال.");
        }
      }
    } catch (e) {
      setError(e.message);
      toast.error(e.message || "Generation failed — see the error banner. No auto-retry.");
    } finally {
      setConfirmed(false);  // require re-confirmation for any next run
    }
  };

  const handleDryRun = async () => {
    setError(null);
    setDryResult(null);
    try {
      const body = JSON.stringify({
        prompt, platform, duration_seconds: duration,
        overlay_title: title, overlay_subtitle: subtitle, overlay_cta: cta,
        watermark, confirm_charge: false,
      });
      const r = await api("/api/preview/video-pilot/generate", {
        method: "POST", body,
      });
      setDryResult(r);
      toast.info("Dry-run complete. No credits spent.");
    } catch (e) {
      setError(e.message);
    }
  };

  const clearCurrentJob = () => {
    stopPolling();
    localStorage.removeItem(PENDING_KEY);
    localStorage.removeItem(IDEMP_KEY);
    setJob(null);
  };

  return (
    <AppShell>
      <div data-testid="video-pilot-page"
           className="max-w-4xl mx-auto py-8 px-6 space-y-6" dir="rtl">
        <Header />
        {capsErr && <ErrorBanner text={capsErr} />}
        {caps && <CapabilityBanner caps={caps} />}

        <section className="space-y-5 bg-white border border-neutral-200 rounded-2xl p-6">
          {/* Prompt */}
          <Field label="وصف الفيديو">
            <textarea
              data-testid="pilot-prompt"
              className="w-full h-32 border border-neutral-300 rounded-xl p-3 text-right"
              dir="rtl"
              placeholder="اكتب طلبك بالعربية — مثال: فيديو سينمائي قصير لمدينة أوروبية عند الغروب مع حركة كاميرا سلسة"
              value={prompt} onChange={(e) => setPrompt(e.target.value)} />
          </Field>

          {/* Platform + Aspect */}
          <Field label="المنصة">
            <div className="flex gap-2 flex-wrap" data-testid="pilot-platform-group">
              {PLATFORMS.map((p) => (
                <button key={p.id}
                  data-testid={`pilot-platform-${p.id}`}
                  onClick={() => setPlatform(p.id)}
                  className={`px-4 py-2 rounded-full border text-sm transition ${
                    platform === p.id
                      ? "bg-[#BC944A] text-white border-[#BC944A]"
                      : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500"}`}>
                  {p.label} · {p.aspect}
                </button>
              ))}
            </div>
            <p className="text-xs text-neutral-500 mt-2" data-testid="pilot-aspect-line">
              نسبة العرض المطلوبة: <b>{platMeta.aspect}</b> (720×1280). لن يتم استبدالها تلقائياً بأبعاد أفقية.
            </p>
          </Field>

          {/* Duration — filtered by platform's allowed_durations if any */}
          <Field label="المدة">
            <div className="flex gap-2" data-testid="pilot-duration-group">
              {DURATIONS
                .filter((d) => {
                  const pm = PLATFORMS.find((p) => p.id === platform);
                  return !pm?.allowed_durations || pm.allowed_durations.includes(d.id);
                })
                .map((d) => (
                <button key={d.id}
                  data-testid={`pilot-duration-${d.id}`}
                  onClick={() => setDuration(d.id)}
                  className={`px-4 py-2 rounded-full border text-sm ${
                    duration === d.id
                      ? "bg-[#121822] text-white border-[#121822]"
                      : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500"}`}>
                  {d.label} · {d.clip_count} مقطع
                </button>
              ))}
            </div>
            {platMeta.id === "landscape_test" && (
              <p className="text-xs text-amber-700 mt-2" data-testid="pilot-landscape-notice">
                هذا الخيار للاختبار الداخلي فقط. المدة مثبتة على 4 ثوانٍ (مقطع Sora واحد)
                وبأبعاد أصلية 1280×720 بدون قص أو تحويل.
              </p>
            )}
          </Field>

          {/* Overlays */}
          <Field label="العنوان (تراكب DAAM)">
            <input data-testid="pilot-overlay-title" dir="rtl"
              className="w-full border border-neutral-300 rounded-xl p-3 text-right"
              value={title} onChange={(e) => setTitle(e.target.value)} placeholder="اكتشف إيطاليا" />
          </Field>
          <Field label="العنوان الفرعي أو الفئة">
            <input data-testid="pilot-overlay-subtitle" dir="rtl"
              className="w-full border border-neutral-300 rounded-xl p-3 text-right"
              value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="سفر · صيف 2026" />
          </Field>
          <Field label="زر الدعوة (CTA)">
            <input data-testid="pilot-overlay-cta" dir="rtl"
              className="w-full border border-neutral-300 rounded-xl p-3 text-right"
              value={cta} onChange={(e) => setCta(e.target.value)} placeholder="احجز رحلتك" />
          </Field>
          <label className="flex items-center gap-3 text-sm text-neutral-700">
            <input type="checkbox" data-testid="pilot-watermark-toggle"
              checked={watermark} onChange={(e) => setWatermark(e.target.checked)} />
            علامة DAAM المائية على الفيديو النهائي
          </label>
        </section>

        {/* Cost preview */}
        {estimate && <CostPanel estimate={estimate} platMeta={platMeta} />}

        {/* Confirmation + Generate */}
        <section className="bg-neutral-50 border border-neutral-200 rounded-2xl p-6 space-y-4">
          <label className="flex items-start gap-3 text-sm text-neutral-800">
            <input type="checkbox" data-testid="pilot-charge-confirm"
              className="mt-1"
              checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
            <span>
              أؤكد صرف <b>{estimate?.billable?.total_cost_sar ?? "-"} SAR</b> على هذه العملية.
              لن يبدأ أي توليد بدون هذا التأكيد الصريح، ولا تتم إعادة المحاولة تلقائياً عند الفشل.
            </span>
          </label>
          <div className="flex gap-3 flex-wrap">
            <button
              data-testid="pilot-dry-run-btn"
              onClick={handleDryRun}
              disabled={!prompt.trim() || pending}
              className="px-5 py-2.5 rounded-full border border-neutral-300 text-sm text-neutral-800 hover:bg-neutral-100 disabled:opacity-50">
              اختبار جاف (بدون صرف)
            </button>
            <button
              data-testid="pilot-generate-btn"
              onClick={handleGenerate}
              disabled={!canGenerate}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition ${
                canGenerate
                  ? "bg-[#BC944A] text-white hover:bg-[#a58038]"
                  : "bg-neutral-200 text-neutral-500 cursor-not-allowed"}`}>
              {pending
                ? (<><Loader2 className="inline w-4 h-4 me-2 animate-spin" />جاري التوليد…</>)
                : "توليد الفيديو الآن"}
            </button>
          </div>
          {!caps?.native_available && (
            <p className="text-xs text-red-700" data-testid="pilot-generate-blocked-line">
              زر التوليد معطّل: يوجد حاجز في القدرات (اطلع على البانر الأحمر أعلاه).
            </p>
          )}
          {pending && (
            <p className="text-xs text-neutral-600" data-testid="pilot-pending-line">
              يوجد طلب قيد التنفيذ — لن يبدأ طلب جديد حتى ينتهي هذا (لا يوجد إعادة محاولة تلقائية).
            </p>
          )}
        </section>

        {error && <ErrorBanner text={error} />}
        {job && <JobPanel job={job} apiBase={API_BASE} onDismiss={clearCurrentJob} />}
        {dryResult && <DryRunPanel result={dryResult} />}
        {recent.length > 0 && <RecentJobsPanel jobs={recent} apiBase={API_BASE} />}
      </div>
    </AppShell>
  );
}

// ─── UI atoms ─────────────────────────────────────────────────────

function Header() {
  return (
    <header className="space-y-1 border-b border-neutral-200 pb-4">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FAF6EE] border border-[#BC944A]/40 text-[#BC944A] text-xs">
        <ShieldAlert className="w-3.5 h-3.5" /> واجهة داخلية · اختبار فقط
      </div>
      <h1 className="text-2xl font-bold text-neutral-900" data-testid="pilot-title">
        DAAM Video Pilot
      </h1>
      <p className="text-sm text-neutral-600">
        ممر توليد فيديو تجريبي — مخصص للفريق داخلياً. لا يظهر للعملاء ولا يعمل تلقائياً.
      </p>
    </header>
  );
}

function CapabilityBanner({ caps }) {
  if (caps.native_available) {
    return (
      <div data-testid="pilot-caps-ok" className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
        ✅ Sora 2 متصل عبر <b>{caps.provider}</b>. الأبعاد الأصلية {caps.target_aspect} ({caps.target_size}) جاهزة اليوم.
      </div>
    );
  }
  return (
    <div data-testid="pilot-caps-blocked" className="rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-800 space-y-2">
      <div className="font-semibold flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> التوليد معطّل: الأبعاد الأصلية {caps.target_aspect} غير متاحة بعد.</div>
      <ul className="list-disc ps-6 space-y-1">
        {caps.blockers.map((b, i) => (
          <li key={i} data-testid={`pilot-blocker-${i}`}>{b}</li>
        ))}
      </ul>
      <p className="text-xs text-red-700">
        لن يتم استبدال الأبعاد تلقائياً بحجم آخر. اختبار الواجهة نفسه متاح باستخدام
        زر "اختبار جاف" — لن يتم صرف أي رصيد.
      </p>
    </div>
  );
}

function CostPanel({ estimate, platMeta }) {
  const b = estimate.billable;
  return (
    <section data-testid="pilot-cost-panel"
             className="rounded-2xl border border-[#BC944A]/40 bg-[#FAF6EE] p-5 text-sm">
      <div className="flex items-center gap-2 mb-3 text-[#BC944A] font-semibold">
        <Info className="w-4 h-4" /> ملخّص التكلفة (قبل التأكيد)
      </div>
      <table className="w-full text-neutral-800 text-sm">
        <tbody>
          <Row k="النسبة" v={`${estimate.aspect} · ${estimate.size}`} tid="pilot-cost-aspect" />
          <Row k="المنصة" v={platMeta.label} tid="pilot-cost-platform" />
          <Row k="عدد المقاطع المولّدة" v={estimate.clip_count} tid="pilot-cost-clips" />
          <Row k="ثواني Sora المُحاسبة" v={b.sora_seconds} tid="pilot-cost-sec" />
          <Row k="تكلفة Sora" v={`${b.sora_cost_sar} SAR`} tid="pilot-cost-sora" />
          <Row k="تكلفة تخطيط Claude" v={`${b.claude_cost_sar} SAR`} tid="pilot-cost-claude" />
          <Row k="المجموع"
               v={<b className="text-[#BC944A]">{b.total_cost_sar} SAR</b>}
               tid="pilot-cost-total" bold />
        </tbody>
      </table>
    </section>
  );
}

function Row({ k, v, tid, bold }) {
  return (
    <tr className="border-b border-[#BC944A]/15 last:border-b-0">
      <td className={`py-2 text-neutral-600 ${bold ? "font-semibold" : ""}`}>{k}</td>
      <td className="py-2 text-left ltr:text-right" data-testid={tid}>{v}</td>
    </tr>
  );
}

function ErrorBanner({ text }) {
  return (
    <div data-testid="pilot-error" className="rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-800">
      {text}
    </div>
  );
}

function JobPanel({ job, apiBase, onDismiss }) {
  const ready  = job.status === "ready";
  const failed = job.status === "failed";
  const pending = !ready && !failed;
  const videoSrc    = ready && job.video_url    ? `${apiBase}${job.video_url}`    : null;
  const downloadSrc = ready && job.download_url ? `${apiBase}${job.download_url}` : null;
  return (
    <section data-testid="pilot-job-panel"
             className="rounded-2xl border border-neutral-200 bg-white p-5 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span data-testid="pilot-job-status" className={`px-2 py-0.5 rounded-full text-xs ${
          ready  ? "bg-emerald-100 text-emerald-800" :
          failed ? "bg-red-100 text-red-800" :
                   "bg-amber-100 text-amber-800"
        }`}>
          {ready ? "READY" : failed ? "FAILED" : (job.status || "PENDING").toUpperCase()}
        </span>
        {pending && <Loader2 className="w-4 h-4 animate-spin text-neutral-500" />}
        <span className="text-xs text-neutral-500 font-mono" data-testid="pilot-job-id">
          {job.job_id}
        </span>
        {onDismiss && (ready || failed) && (
          <button onClick={onDismiss} data-testid="pilot-job-dismiss"
                  className="ms-auto text-xs text-neutral-500 hover:text-neutral-800 underline">
            إخفاء
          </button>
        )}
      </div>

      {pending && (
        <p className="text-sm text-neutral-700" data-testid="pilot-job-pending-hint">
          يتم التوليد في الخلفية. يمكنك تحديث الصفحة أو الرجوع لاحقاً — سيتم استئناف المتابعة تلقائياً.
        </p>
      )}

      {failed && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 space-y-1"
             data-testid="pilot-job-error">
          <div><b>سبب الفشل:</b> {job.error_reason || "غير معروف"}</div>
          <div>{job.error_message || "—"}</div>
        </div>
      )}

      {ready && videoSrc && (
        <>
          <video data-testid="pilot-job-video"
                 controls preload="metadata"
                 className="w-full rounded-xl border border-neutral-200 bg-black"
                 src={videoSrc} />
          <div className="flex gap-3 flex-wrap">
            <a data-testid="pilot-job-play" href={videoSrc}
               target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#121822] text-white text-sm">
              <Play className="w-4 h-4" /> تشغيل بحجم كامل
            </a>
            <a data-testid="pilot-job-download" href={downloadSrc}
               className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-neutral-300 text-sm">
              <Download className="w-4 h-4" /> تنزيل MP4
            </a>
          </div>
        </>
      )}
    </section>
  );
}

function DryRunPanel({ result }) {
  return (
    <section data-testid="pilot-dryrun-panel"
             className="rounded-2xl border border-neutral-200 bg-white p-5 space-y-3">
      <div className="flex items-center gap-2">
        <span className="px-2 py-0.5 rounded-full text-xs bg-neutral-100 text-neutral-600">
          DRY-RUN
        </span>
        <span className="text-sm text-neutral-800">لم يتم صرف أي رصيد.</span>
      </div>
      <details className="text-xs text-neutral-700">
        <summary className="cursor-pointer">حمولة الاستجابة الكاملة</summary>
        <pre className="mt-2 whitespace-pre-wrap break-all bg-neutral-50 p-3 rounded-lg">
          {JSON.stringify(result, null, 2)}
        </pre>
      </details>
    </section>
  );
}

function RecentJobsPanel({ jobs, apiBase }) {
  return (
    <section data-testid="pilot-recent-panel"
             className="rounded-2xl border border-neutral-200 bg-white p-5 space-y-3">
      <div className="flex items-center gap-2 text-neutral-800 font-semibold">
        <History className="w-4 h-4" /> جلسات سابقة من Video Pilot
      </div>
      <ul className="divide-y divide-neutral-100">
        {jobs.map((j) => {
          const ready = j.status === "ready";
          const videoSrc    = ready && j.video_url    ? `${apiBase}${j.video_url}`    : null;
          const downloadSrc = ready && j.download_url ? `${apiBase}${j.download_url}` : null;
          return (
            <li key={j.job_id} className="py-3 flex items-start gap-3 flex-wrap"
                data-testid={`pilot-recent-${j.job_id}`}>
              <span className={`px-2 py-0.5 rounded-full text-xs shrink-0 ${
                ready ? "bg-emerald-100 text-emerald-800" :
                j.status === "failed" ? "bg-red-100 text-red-800" :
                "bg-amber-100 text-amber-800"
              }`}>{(j.status || "?").toUpperCase()}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-neutral-800 truncate" dir="rtl">
                  {j.prompt || j.job_id}
                </div>
                <div className="text-xs text-neutral-500 font-mono">
                  {j.job_id.slice(0, 8)}… · {j.aspect_ratio} · {j.duration_seconds}s
                </div>
              </div>
              {ready && videoSrc && (
                <div className="flex gap-2 shrink-0">
                  <a href={videoSrc} target="_blank" rel="noreferrer"
                     data-testid={`pilot-recent-play-${j.job_id}`}
                     className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#121822] text-white text-xs">
                    <Play className="w-3 h-3" /> تشغيل
                  </a>
                  <a href={downloadSrc}
                     data-testid={`pilot-recent-download-${j.job_id}`}
                     className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-neutral-300 text-xs">
                    <Download className="w-3 h-3" /> تنزيل
                  </a>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-sm font-medium text-neutral-800 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
